import React, { useState } from 'react'
import { Input, Button, Select, Icon, Modal } from 'antd'

const { Option } = Select

// ─── DialogueEditor ───────────────────────────────────────────────────────────
// Props:
//   dialogue    = { [characterKey]: "dialogue text", ... }
//   onChange    = (newDialogue) => void
//   disabled    = bool
//   formData    = { characterMap, characterList, selectedCharacter, ... }
// ─────────────────────────────────────────────────────────────────────────────

const DialogueEditor = ({
  dialogue = {},
  onChange,
  disabled,
  formData = {}
}) => {
  let characterMap = formData?.characterMap || {}
  if (typeof characterMap === 'string') {
    try {
      characterMap = JSON.parse(characterMap)
    } catch (e) {
      characterMap = {}
    }
  }

  const [isOpen, setIsOpen] = useState(false)
  const [isAddingModalOpen, setIsAddingModalOpen] = useState(false)
  const [selectedCharKey, setSelectedCharKey] = useState(undefined)
  const [newDialogueText, setNewDialogueText] = useState('')
  const [isHovered, setIsHovered] = useState(false)

  // ── Read helpers ──────────────────────────────────────────────────────────────
  const entries = Object.entries(dialogue)
  const hasDialogue = entries.length > 0
  const allCharacters = Object.entries(characterMap)

  // ── Character image helper ────────────────────────────────────────────────────
  const getCharacterImage = key => {
    const characterList = formData?.characterList || []
    const selectedCharacters = formData?.selectedCharacter || []
    const mapping = selectedCharacters.find(
      m => m.original === key || m.current === key
    )
    const resolvedName = mapping
      ? mapping.current.replace(/^__|__$/g, '')
      : key.replace(/^__|__$/g, '')
    const characterId = mapping?.tag || null
    let character = null
    if (characterId) {
      character = characterList.find(c => c.character_id === characterId)
    }
    if (!character && resolvedName) {
      character = characterList.find(c => c.name === resolvedName)
    }
    return character?.attributes?.profile_pic || null
  }

  // ── Write helpers ─────────────────────────────────────────────────────────────
  function updateDialogue(characterKey, text) {
    onChange({ ...dialogue, [characterKey]: text })
  }

  function deleteDialogue(characterKey) {
    Modal.confirm({
      title: 'Delete Dialogue?',
      content:
        'Are you sure you want to remove this dialogue? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      centered: true,
      maskClosable: true,
      onOk() {
        const next = { ...dialogue }
        delete next[characterKey]
        onChange(next)
      }
    })
  }

  function addDialogue() {
    if (!selectedCharKey) return
    onChange({ ...dialogue, [selectedCharKey]: newDialogueText })
    closeModal()
  }

  function closeModal() {
    setSelectedCharKey(undefined)
    setNewDialogueText('')
    setIsAddingModalOpen(false)
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      {/* ── Header / toggle ── */}
      <div
        style={styles.header(isHovered)}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={styles.headerTitle}>
          <Icon
            type="message"
            style={styles.headerIcon(hasDialogue && !isOpen)}
          />
          <span style={styles.headerLabel(hasDialogue && !isOpen)}>
            Shot Dialogue
          </span>
          {hasDialogue && !isOpen && (
            <span style={styles.badge}>{entries.length}</span>
          )}
        </div>
        <Icon
          type={isOpen ? 'up' : 'down'}
          style={styles.chevron(hasDialogue && !isOpen)}
        />
      </div>

      {/* ── Expanded body ── */}
      {isOpen && (
        <div style={styles.body}>
          {/* ── Empty state ── */}
          {!hasDialogue && (
            <div style={styles.emptyState}>
              <Icon type="message" style={{ fontSize: 16, color: '#d9d9d9' }} />
              <span style={styles.emptyText}>No dialogue added yet</span>
            </div>
          )}

          {/* ── Existing dialogue entries ── */}
          {hasDialogue && (
            <div style={styles.list}>
              {entries.map(([key, text], idx) => {
                const displayName =
                  characterMap[key] || key.replace(/^__|__$/g, '')
                const charImage = getCharacterImage(key)
                const initials = displayName
                  .split(' ')
                  .slice(0, 2)
                  .map(w => w[0])
                  .join('')
                  .toUpperCase()
                const isLast = idx === entries.length - 1

                return (
                  <div key={key} style={styles.entry(isLast)}>
                    {/* ── Avatar ── */}
                    <div style={styles.avatar}>
                      {charImage ? (
                        <img
                          src={charImage}
                          alt={displayName}
                          style={styles.avatarImg}
                        />
                      ) : (
                        <span style={styles.avatarInitials}>{initials}</span>
                      )}
                    </div>

                    {/* ── Content ── */}
                    <div style={styles.entryContent}>
                      <div style={styles.entryHeader}>
                        <div style={styles.entryMeta}>
                          <span style={styles.charName}>{displayName}</span>
                          <span style={styles.charKey}>{key}</span>
                        </div>
                        {!disabled && (
                          <Icon
                            type="delete"
                            onClick={() => deleteDialogue(key)}
                            style={styles.deleteIcon}
                            title="Delete Dialogue"
                          />
                        )}
                      </div>
                      <Input.TextArea
                        value={text}
                        disabled={disabled}
                        onChange={e => updateDialogue(key, e.target.value)}
                        autoSize={{ minRows: 1, maxRows: 6 }}
                        style={styles.textarea}
                        placeholder="Enter dialogue..."
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* ── Add dialogue button ── */}
          {!disabled && (
            <Button
              type="dashed"
              icon="plus"
              size="small"
              block
              onClick={() => setIsAddingModalOpen(true)}
              style={styles.addButton}
            >
              Add Dialogue
            </Button>
          )}
        </div>
      )}

      {/* ── Add Dialogue Modal ── */}
      <Modal
        title="Add Character Dialogue"
        visible={isAddingModalOpen}
        onOk={addDialogue}
        onCancel={closeModal}
        okText="Add"
        cancelText="Cancel"
        okButtonProps={{
          disabled: !selectedCharKey || !newDialogueText?.trim()
        }}
        destroyOnClose
        centered
        width={420}
        bodyStyle={{ paddingTop: 16, paddingBottom: 16 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <div style={styles.modalLabel}>
              Select Character <span style={{ color: '#ff4d4f' }}>*</span>
            </div>
            <Select
              showSearch
              placeholder="Search and select character…"
              optionFilterProp="children"
              value={selectedCharKey}
              onChange={setSelectedCharKey}
              style={{ width: '100%' }}
            >
              {allCharacters.map(([key, name]) => (
                <Option key={key} value={key}>
                  {name} ({key})
                </Option>
              ))}
            </Select>
          </div>
          <div>
            <div style={styles.modalLabel}>
              Dialogue Text <span style={{ color: '#ff4d4f' }}>*</span>
            </div>
            <Input.TextArea
              placeholder="Enter dialogue here…"
              value={newDialogueText}
              onChange={e => setNewDialogueText(e.target.value)}
              autoSize={{ minRows: 4, maxRows: 10 }}
              disabled={!selectedCharKey}
              style={{ fontSize: 13 }}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  container: {
    border: '1px solid #f0f0f0',
    borderRadius: 12,
    overflow: 'hidden',
    marginTop: 12,
    backgroundColor: '#fafafa'
  },
  header: isHovered => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    cursor: 'pointer',
    background: isHovered ? '#e8e8e8' : '#f0f0f0',
    userSelect: 'none',
    borderBottom: '1px solid #e8e8e8',
    transition: 'all 0.2s ease'
  }),
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  headerIcon: active => ({
    color: active ? '#1890ff' : '#8c8c8c',
    fontSize: 12,
    transition: 'color 0.2s ease'
  }),
  headerLabel: active => ({
    fontSize: 10,
    fontWeight: 700,
    color: active ? '#1890ff' : '#8c8c8c',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    transition: 'color 0.2s ease'
  }),
  badge: {
    background: '#1890ff',
    color: '#fff',
    fontSize: 10,
    fontWeight: 600,
    height: 16,
    minWidth: 16,
    padding: '0 5px',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 0 0 1px #fff',
    letterSpacing: 'normal',
    lineHeight: 1
  },
  chevron: active => ({
    fontSize: 12,
    color: active ? '#1890ff' : '#bfbfbf',
    transition: 'transform 0.3s ease'
  }),
  body: {
    padding: '0 12px 10px'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 0',
    gap: 6
  },
  emptyText: {
    fontSize: 11,
    fontStyle: 'italic',
    color: '#bfbfbf'
  },
  list: {
    display: 'flex',
    flexDirection: 'column'
  },
  entry: isLast => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    paddingTop: 10,
    paddingBottom: 10,
    borderBottom: isLast ? 'none' : '1px solid #f0f0f0'
  }),
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 6,
    border: '1px solid #d9d9d9',
    overflow: 'hidden',
    flexShrink: 0,
    background: '#f0f2f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  avatarInitials: {
    fontSize: 11,
    fontWeight: 700,
    color: '#1890ff',
    letterSpacing: '-0.02em'
  },
  entryContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  entryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  entryMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    overflow: 'hidden'
  },
  charName: {
    fontSize: 12,
    fontWeight: 600,
    color: '#262626',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  charKey: {
    fontSize: 10,
    color: '#1890ff',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  deleteIcon: {
    color: '#bfbfbf',
    cursor: 'pointer',
    fontSize: 13,
    padding: '2px',
    transition: 'color 0.2s',
    flexShrink: 0
  },
  textarea: {
    fontSize: 12,
    color: '#434343',
    resize: 'none',
    boxShadow: 'none',
    border: '1px solid #ebebeb',
    borderRadius: 6,
    background: '#fff',
    lineHeight: 1.6
  },
  addButton: {
    color: '#8c8c8c',
    borderColor: '#e8e8e8',
    background: 'transparent',
    height: 30,
    fontSize: 12,
    marginTop: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderRadius: 6
  },
  modalLabel: {
    marginBottom: 6,
    fontSize: 13,
    fontWeight: 500,
    color: '#262626'
  }
}

export default DialogueEditor
